import React, { Component } from 'react';
import { connect } from 'react-redux';

import { Column } from '@appcues/component-library';

import { findSubscription } from 'actions/newBilling';
import { navigate } from 'actions/routing';
import Loader from 'components/Common/Loader';
import BillingDetails from 'components/Subscription/BillingDetails';
import BillingHistory from 'components/Subscription/BillingHistory';
import CanceledNotice from 'components/Subscription/CanceledNotice';
import { CurrentPeriod } from 'components/Subscription/CurrentPeriod';
import FreeTrial from 'components/Subscription/FreeTrial';
import InvoiceMessage from 'components/Subscription/InvoiceMessage';
import { YourPlan } from 'components/Subscription/YourPlan';
import ManageSubscription from 'components/Subscription/ManageSubscription';
import {
  selectAccountMeta,
  selectAccountMetaSynced,
} from 'reducers/account/meta';
import { selectUser, selectUserId, selectUserSynced } from 'reducers/user';
import {
  selectAccountAllTimeStats,
  selectAccountAllTimeStatsSynced,
} from 'reducers/account/allTimeStats';
import { selectBilling } from 'reducers/billing';
import { selectAccountEntitlementsProcessed } from 'reducers/entitlements';
import { selectMausProcessed } from 'selectors/entitlements';
import { selectCurrentPlanData } from 'selectors/billing';
import { updateCard } from 'utils/stripe';

import { StyledNotice, PlanUsage } from './styled';

export class Subscription extends Component {
  state = {
    showSubscriptionBanner: false,
    subscriptionLoaded: false,
  };

  componentDidMount() {
    const { onLoadSubscription, location } = this.props;

    const params = new URLSearchParams(location?.search);
    const id = params.get('id');
    if (id) {
      this.setState({ showSubscriptionBanner: true, subscriptionLoaded: true });
      onLoadSubscription({ id });
    }
  }

  componentDidUpdate() {
    this.loadStripeSubscriptionIfNeeded();
  }

  navigateToStory = () => {
    const { navigate: navigateTo } = this.props;
    navigateTo('/subscription/story');
  };

  SubscriptionContent = ({
    isStripeCustomer,
    currentStripeId,
    entitlementsProcessed = [],
    mausProcessed = {},
    subscription,
    planData = {},
  }) => {
    const { accountMeta, allTimeStats, billing, user, userId } = this.props;
    const { showSubscriptionBanner } = this.state;
    const {
      canceledAt,
      id: accountId,
      isTrial,
      stripeSubEndedAt: accountCanceledAt,
    } = accountMeta;

    const userWithAccountId = user && {
      accountId,
      email: user.email,
      id: userId.replace('simplelogin:', ''),
    };

    if (isTrial && !canceledAt) {
      return <FreeTrial />;
    }

    return (
      <Column>
        {showSubscriptionBanner && !canceledAt && (
          <StyledNotice
            type="success"
            onDismiss={() => this.setState({ showSubscriptionBanner: false })}
          >
            🎉 <strong>Woo hoo!</strong> You’ve successfully upgraded. A
            confirmation email was sent to <strong>{user.email}</strong>
          </StyledNotice>
        )}

        {canceledAt && (
          <CanceledNotice
            canceledAt={canceledAt}
            billingDetails={billing.details}
          />
        )}

        {Object.keys(planData).length > 0 && (
          <PlanUsage>
            <YourPlan planData={planData} mausProcessed={mausProcessed} />

            <CurrentPeriod
              accountId={accountMeta.id}
              anonymousMaus={allTimeStats.monthlyAnonymousUsers}
              entitlementsProcessed={entitlementsProcessed}
              mausProcessed={mausProcessed}
              monthlyActiveUsers={allTimeStats.monthlyWebsiteUsers}
              uuid={accountMeta.uuid}
            />
          </PlanUsage>
        )}

        <>
          {isStripeCustomer && billing.details && (
            <BillingDetails
              billingDetails={billing.details}
              currentStripeId={currentStripeId}
              updateCard={updateCard}
              userWithAccountId={userWithAccountId}
            />
          )}

          {isStripeCustomer && billing.history && (
            <BillingHistory billingHistory={billing.history} />
          )}

          {!isStripeCustomer && <InvoiceMessage />}
        </>

        {subscription && !canceledAt && (
          <ManageSubscription
            accountCanceledAt={accountCanceledAt}
            billingDetails={billing.details}
            navigateToStory={this.navigateToStory}
          />
        )}
      </Column>
    );
  };

  loadStripeSubscriptionIfNeeded() {
    const { onLoadSubscription, accountMeta, location } = this.props;
    const { subscriptionLoaded } = this.state;
    const params = new URLSearchParams(location?.search);

    if (accountMeta.stripeId && !subscriptionLoaded && !params.get('id')) {
      this.setState({ subscriptionLoaded: true });
      onLoadSubscription({ stripeId: accountMeta.stripeId });
    }
  }

  render() {
    const { accountMeta, isSynced } = this.props;

    const currentStripeId = accountMeta.stripeId;
    const isStripeCustomer =
      currentStripeId && currentStripeId.startsWith('cus_');

    if (!isSynced) {
      return <Loader />;
    }

    return (
      <this.SubscriptionContent
        isStripeCustomer={isStripeCustomer}
        currentStripeId={currentStripeId}
        {...this.props}
      />
    );
  }
}

const mapStateToProps = state => {
  const isSynced =
    selectUserSynced(state) &&
    selectAccountMetaSynced(state) &&
    selectAccountAllTimeStatsSynced(state);

  return {
    accountMeta: selectAccountMeta(state),
    allTimeStats: selectAccountAllTimeStats(state),
    billing: selectBilling(state),
    entitlementsProcessed: selectAccountEntitlementsProcessed(state),
    isSynced,
    planData: selectCurrentPlanData(state),
    mausProcessed: selectMausProcessed(state),
    subscription: state.newBilling.subscription,
    user: selectUser(state),
    userId: selectUserId(state),
  };
};

const mapDispatchToProps = dispatch => {
  return {
    navigate: path => dispatch(navigate(path)),
    onLoadSubscription: payload => dispatch(findSubscription(payload)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Subscription);
